/** Product tags for routine-match, lead flows (regimen / skin analyzer), and storefront featured. */

/** Legacy public-catalog featured filter; not an admin tag famille. */
export const STOREFRONT_FEATURED_TAG = 'storefront:featured';

export const CATALOG_SKIN_TYPES = ['seche', 'grasse', 'mixte', 'sensible', 'normale'] as const;
export const CATALOG_CONCERNS = [
  'hydratation',
  'antiage',
  'imperfections',
  'teint',
  'protection',
  'sensibilite',
] as const;

export type CatalogConcernCode = (typeof CATALOG_CONCERNS)[number];

export type CatalogConcernOption = { value: CatalogConcernCode; label: string };

/** Storefront / mega / PLP display labels for `?concern=` values. */
export const CATALOG_CONCERN_LABELS: Record<CatalogConcernCode, string> = {
  hydratation: 'Hydratation',
  antiage: 'Anti-âge',
  imperfections: 'Imperfections',
  teint: 'Éclat',
  protection: 'Protection',
  sensibilite: 'Sensibilité',
};

export function catalogConcernOptions(): CatalogConcernOption[] {
  return CATALOG_CONCERNS.map((value) => ({
    value,
    label: CATALOG_CONCERN_LABELS[value],
  }));
}

export function catalogConcernLabel(value: string): string | undefined {
  if ((CATALOG_CONCERNS as readonly string[]).includes(value)) {
    return CATALOG_CONCERN_LABELS[value as CatalogConcernCode];
  }
  return undefined;
}
export const CATALOG_TEXTURES = ['legere', 'confort', 'sans-parfum'] as const;

export type RoutineStepDef = {
  step: string;
  tag: string;
  dayExpress: boolean;
  dayComplete: boolean;
  nightExpress: boolean;
  nightComplete: boolean;
};

/** Steps used by `GET /catalog/routine-match` (Regimen Builder + skin analyzer results). */
export const ROUTINE_STEPS: RoutineStepDef[] = [
  { step: 'Nettoyant', tag: 'routine:nettoyant', dayExpress: true, dayComplete: true, nightExpress: true, nightComplete: true },
  { step: 'Brume / essence', tag: 'routine:brume', dayExpress: false, dayComplete: true, nightExpress: false, nightComplete: false },
  { step: 'Sérum ciblé', tag: 'routine:serum', dayExpress: true, dayComplete: true, nightExpress: false, nightComplete: false },
  { step: 'Contour yeux', tag: 'routine:contour-yeux', dayExpress: false, dayComplete: true, nightExpress: false, nightComplete: false },
  { step: 'SPF', tag: 'routine:spf', dayExpress: true, dayComplete: true, nightExpress: false, nightComplete: false },
  { step: 'Double nettoyage', tag: 'routine:double-nettoyage', dayExpress: false, dayComplete: false, nightExpress: true, nightComplete: true },
  { step: 'Tonique', tag: 'routine:tonique', dayExpress: false, dayComplete: false, nightExpress: false, nightComplete: true },
  { step: 'Actif nuit', tag: 'routine:actif-nuit', dayExpress: false, dayComplete: false, nightExpress: false, nightComplete: true },
  { step: 'Soin traitant', tag: 'routine:soin-traitant', dayExpress: false, dayComplete: false, nightExpress: true, nightComplete: false },
  { step: 'Soin réparateur', tag: 'routine:soin-reparateur', dayExpress: false, dayComplete: false, nightExpress: false, nightComplete: true },
  { step: 'Crème barrière', tag: 'routine:creme-barriere', dayExpress: false, dayComplete: false, nightExpress: true, nightComplete: true },
];

/** Canonical `routine:*` tags for regimen builder / `routine-match`. */
export const ROUTINE_STEP_TAG_VALUES: readonly string[] = ROUTINE_STEPS.map((s) => s.tag);

export const REGIMEN_BUILDER_LEAD_FLOW_SLUG = 'regimenbuilder' as const;

export function regimenBuilderPublishBlockedReason(routineTaggedProductCount: number): string | null {
  return routineMatchLeadFlowPublishBlockedReason(routineTaggedProductCount, {
    flowLabel: 'Regimen Builder',
  });
}

export function routineMatchLeadFlowPublishBlockedReason(
  routineTaggedProductCount: number,
  opts?: { flowLabel?: string },
): string | null {
  if (routineTaggedProductCount > 0) return null;
  const label = opts?.flowLabel ?? 'Ce flow';
  return (
    `${label} : aucun produit vitrine prêt n’a de tag routine:* (ex. routine:serum). ` +
    'Sans ces tags, le matching produit reste vide. Ajoutez des tags sur des produits visibles boutique, ou Taxonomie → Tags.'
  );
}

export type CatalogTagTemplate = { value: string; label: string };

export type CatalogTagTemplateGroup = {
  id: string;
  label: string;
  description: string;
  tags: CatalogTagTemplate[];
};

/** Family shape from `GET /catalog/admin/tags/families` (admin UI source of truth). */
export type CatalogTagFamilyRef = {
  code: string;
  name: string;
  tags: CatalogTagTemplate[];
};

const skinLabel: Record<string, string> = {
  seche: 'Peau sèche',
  grasse: 'Peau grasse',
  mixte: 'Peau mixte',
  sensible: 'Peau sensible',
  normale: 'Peau normale',
};

const concernLabel: Record<string, string> = { ...CATALOG_CONCERN_LABELS };

const textureLabel: Record<string, string> = {
  legere: 'Texture légère',
  confort: 'Texture confort',
  'sans-parfum': 'Sans parfum',
};

/** Canonical seed templates — install via Config initiale → Tags, not UI hardcoding. */
export const CATALOG_PRODUCT_TAG_TEMPLATE_GROUPS: CatalogTagTemplateGroup[] = [
  {
    id: 'routine',
    label: 'Étape routine',
    description: 'Obligatoire pour apparaître dans Regimen Builder (`routine-match`). Un seul tag étape par produit.',
    tags: ROUTINE_STEPS.map((s) => ({ value: s.tag, label: s.step })),
  },
  {
    id: 'skin',
    label: 'Type de peau',
    description: 'Réponses quiz regimen / analyse peau (`skin:*`).',
    tags: CATALOG_SKIN_TYPES.map((v) => ({ value: `skin:${v}`, label: skinLabel[v] ?? v })),
  },
  {
    id: 'concern',
    label: 'Préoccupation',
    description: 'Filtre PLP boutique `?concern=` (hydratation, antiage, imperfections, teint, protection, sensibilite).',
    tags: CATALOG_CONCERNS.map((v) => ({ value: `concern:${v}`, label: concernLabel[v] ?? v })),
  },
  {
    id: 'texture',
    label: 'Texture',
    description: 'Aligné sur les choix `texture` du flow (`texture:*`).',
    tags: CATALOG_TEXTURES.map((v) => ({ value: `texture:${v}`, label: textureLabel[v] ?? v })),
  },
  {
    id: 'storefront',
    label: 'Vitrine',
    description: 'Accueil boutique (`/`) et PLP `/selection`.',
    tags: [{ value: STOREFRONT_FEATURED_TAG, label: 'Mis en avant storefront' }],
  },
];

export type ProductTagGuidanceIssue = {
  level: 'error' | 'warning' | 'info';
  message: string;
};

export type ProductTagGuidance = {
  issues: ProductTagGuidanceIssue[];
  hasRoutineStep: boolean;
  hasSkinType: boolean;
  hasConcern: boolean;
  hasTexture: boolean;
};

/**
 * @deprecated Prefer API families for product profile UI. Kept for seed/matching helpers.
 */
export const PRODUCT_TAG_PROFILE_GROUP_IDS = ['routine', 'skin', 'concern', 'texture'] as const;

export type ProductTagProfileGroupId = (typeof PRODUCT_TAG_PROFILE_GROUP_IDS)[number];

/** Prefix before `:` for namespaced tags (`routine:spf` → `routine`). */
export function catalogTagFamilyCode(tag: string): string | null {
  if (tag.startsWith('storefront:')) return null;
  const i = tag.indexOf(':');
  if (i <= 0) return null;
  return tag.slice(0, i);
}

export function catalogTagDisplayLabel(tag: string): string {
  for (const g of CATALOG_PRODUCT_TAG_TEMPLATE_GROUPS) {
    const tpl = g.tags.find((t) => t.value === tag);
    if (tpl) return tpl.label;
  }
  const colon = tag.indexOf(':');
  if (colon >= 0) {
    const suffix = tag.slice(colon + 1).replace(/-/g, ' ');
    return suffix.charAt(0).toUpperCase() + suffix.slice(1);
  }
  return tag;
}

export function catalogTagDisplayLabelFromFamilies(
  tag: string,
  families: readonly CatalogTagFamilyRef[],
): string {
  for (const f of families) {
    const tpl = f.tags.find((t) => t.value === tag);
    if (tpl) return tpl.label;
  }
  return catalogTagDisplayLabel(tag);
}

/** @deprecated Prefer catalogTagFamilyCode + API families. */
export function catalogTagGroupId(tag: string): ProductTagProfileGroupId | null {
  if (tag.startsWith('routine:')) return 'routine';
  if (tag.startsWith('skin:')) return 'skin';
  if (tag.startsWith('concern:')) return 'concern';
  if (tag.startsWith('texture:')) return 'texture';
  return null;
}

/** @deprecated Prefer catalogUngroupedTagsFromFamilies. */
export function catalogUngroupedTags(tags: string[]): string[] {
  return tags.filter((t) => !catalogTagGroupId(t) && !t.startsWith('storefront:'));
}

export function catalogUngroupedTagsFromFamilies(
  tags: string[],
  families: readonly CatalogTagFamilyRef[],
): string[] {
  const codes = new Set(families.map((f) => f.code));
  return tags.filter((t) => {
    if (t.startsWith('storefront:')) return false;
    const code = catalogTagFamilyCode(t);
    if (!code) return true;
    return !codes.has(code);
  });
}

/** Replace any tag in the same famille (prefix), then add. */
export function catalogUpsertTag(tags: string[], tag: string): string[] {
  const code = catalogTagFamilyCode(tag);
  const without = code
    ? tags.filter((t) => catalogTagFamilyCode(t) !== code)
    : tags.filter((t) => t !== tag);
  if (without.includes(tag)) return without;
  return [...without, tag];
}

/** @deprecated Prefer catalogTagsInFamily. */
export function catalogTagsInGroup(
  tags: string[],
  groupId: ProductTagProfileGroupId,
): { value: string; label: string }[] {
  return tags
    .filter((t) => t.startsWith(`${groupId}:`))
    .map((t) => ({
      value: t,
      label: catalogTagDisplayLabel(t),
    }));
}

export function catalogTagsInFamily(
  tags: string[],
  family: CatalogTagFamilyRef,
): { value: string; label: string }[] {
  return tags
    .filter((t) => catalogTagFamilyCode(t) === family.code)
    .map((t) => ({
      value: t,
      label: catalogTagDisplayLabelFromFamilies(t, [family]),
    }));
}

function hasPrefix(tags: string[], prefix: string): boolean {
  return tags.some((t) => t.startsWith(prefix));
}

/**
 * Soft guidance against installed families only.
 * No phantom routine/skin/concern/texture rows when those families are absent.
 */
export function analyzeCatalogProductTagsAgainstFamilies(
  tags: string[],
  families: readonly CatalogTagFamilyRef[],
  opts?: { storefrontVisible?: boolean },
): { issues: ProductTagGuidanceIssue[]; hasRoutineStep: boolean } {
  const storefrontVisible = opts?.storefrontVisible !== false;
  const codes = new Set(families.map((f) => f.code));
  const hasRoutineStep = hasPrefix(tags, 'routine:');
  const issues: ProductTagGuidanceIssue[] = [];

  if (storefrontVisible && codes.has('routine') && !hasRoutineStep) {
    issues.push({
      level: 'info',
      message:
        'Aucune étape routine — produit absent des flows Regimen Builder et analyse peau.',
    });
  }

  return { issues, hasRoutineStep };
}

/** @deprecated Prefer analyzeCatalogProductTagsAgainstFamilies with API families. */
export function analyzeCatalogProductTags(
  tags: string[],
  opts?: { storefrontVisible?: boolean },
): ProductTagGuidance {
  const storefrontVisible = opts?.storefrontVisible !== false;
  const hasRoutineStep = hasPrefix(tags, 'routine:');
  const hasSkinType = hasPrefix(tags, 'skin:');
  const hasConcern = hasPrefix(tags, 'concern:');
  const hasTexture = hasPrefix(tags, 'texture:');

  const issues: ProductTagGuidanceIssue[] = [];

  if (storefrontVisible) {
    if (!hasRoutineStep) {
      issues.push({
        level: 'error',
        message:
          'Aucune étape routine — produit absent des flows Regimen Builder et analyse peau.',
      });
    } else {
      if (!hasSkinType) {
        issues.push({
          level: 'warning',
          message: 'Type de peau manquant — matching quiz limité à l’étape routine.',
        });
      }
      if (!hasConcern) {
        issues.push({
          level: 'warning',
          message: 'Préoccupation manquante — matching quiz incomplet.',
        });
      }
      if (!hasTexture) {
        issues.push({
          level: 'warning',
          message: 'Texture manquante — matching quiz incomplet.',
        });
      }
    }
  }

  return {
    issues,
    hasRoutineStep,
    hasSkinType,
    hasConcern,
    hasTexture,
  };
}

/** Split product tag tokens into known vs unknown against CatalogTag.value set. */
export function partitionCatalogTagsByKnownValues(
  tags: string[],
  knownValues: ReadonlySet<string>,
): { known: string[]; unknown: string[] } {
  const known: string[] = [];
  const unknown: string[] = [];
  for (const t of tags) {
    if (!t) continue;
    if (t.startsWith('storefront:') || knownValues.has(t)) known.push(t);
    else unknown.push(t);
  }
  return { known, unknown };
}

export type PrefillTagGap = {
  value: string;
  label: string;
  familyCode: string;
  familyName: string;
  familyPresent: boolean;
  tagPresent: boolean;
};

function familyMetaFromTemplates(familyCode: string): { name: string; description: string } {
  const tpl = CATALOG_PRODUCT_TAG_TEMPLATE_GROUPS.find((g) => g.id === familyCode);
  if (tpl) return { name: tpl.label, description: tpl.description };
  const name = familyCode.charAt(0).toUpperCase() + familyCode.slice(1).replace(/-/g, ' ');
  return { name, description: '' };
}

/**
 * Compare inferred OBF/import tags to installed families.
 * Known tags apply immediately; gaps drive « Créer » banners (family and/or value).
 */
export function analyzePrefillTagsAgainstFamilies(
  inferredTags: string[],
  families: readonly CatalogTagFamilyRef[],
): { known: string[]; gaps: PrefillTagGap[] } {
  const familyByCode = new Map(families.map((f) => [f.code, f]));
  const knownValues = new Set(families.flatMap((f) => f.tags.map((t) => t.value)));
  const known: string[] = [];
  const gaps: PrefillTagGap[] = [];
  const seenGap = new Set<string>();

  for (const raw of inferredTags) {
    const value = raw.trim();
    if (!value || value.startsWith('storefront:')) continue;
    if (knownValues.has(value)) {
      if (!known.includes(value)) known.push(value);
      continue;
    }
    const familyCode = catalogTagFamilyCode(value);
    if (!familyCode) continue;
    if (seenGap.has(value)) continue;
    seenGap.add(value);
    const family = familyByCode.get(familyCode);
    const meta = familyMetaFromTemplates(familyCode);
    gaps.push({
      value,
      label: catalogTagDisplayLabelFromFamilies(value, families),
      familyCode,
      familyName: family?.name ?? meta.name,
      familyPresent: Boolean(family),
      tagPresent: false,
    });
  }

  return { known, gaps };
}

export function catalogTagFamilySeedMeta(familyCode: string): {
  code: string;
  name: string;
  description: string;
} {
  const meta = familyMetaFromTemplates(familyCode);
  return { code: familyCode, name: meta.name, description: meta.description };
}
